import { FC, memo, useMemo } from 'react';

import { LineupCardProps, LineupMember } from './types';
import { PunchupTile } from 'features/common/components/Punchup/primitives/display/PunchupTile';
import styles from './PunchupLineupCard.module.css';

/** Discs that fit the 185px stat-tile column before the row needs a "+N" summary. */
const MAX_VISIBLE_AVATARS = 4;

/**
 * First letter of the first and last word of a name ("Sam Ruiz Vega" → "SV"). Falls back to a
 * neutral glyph for a blank/whitespace name so a disc is never empty.
 */
export const initialsOf = (name: string): string => {
	const words = name.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return '·';
	const first = words[0][0];
	const last = words.length > 1 ? words[words.length - 1][0] : '';
	return `${first}${last}`.toUpperCase();
};

/**
 * `imageUrl` arrives on an untrusted cross-frame payload. Only http(s), protocol-relative, and
 * root-relative URLs reach an <img src>; anything else (a `javascript:` / `data:` string) degrades
 * to the initials disc.
 */
const isRenderableImageUrl = (url: string | null | undefined): url is string =>
	typeof url === 'string' && (/^https?:\/\//i.test(url) || url.startsWith('//') || url.startsWith('/'));

const LineupAvatar: FC<{ member: LineupMember }> = ({ member }) =>
	isRenderableImageUrl(member.imageUrl) ? (
		<img className={styles.avatar} src={member.imageUrl} alt={member.name} title={member.name} />
	) : (
		/* role="img" + aria-label makes the disc announce the person rather than the two letters. */
		<span className={styles.avatar} role="img" aria-label={member.name} title={member.name}>
			{initialsOf(member.name)}
		</span>
	);

/**
 * PunchupLineupCard — a highlight tile whose value is a cluster of overlapping avatars instead of
 * a stat number.
 *
 * It is a `PunchupTile` in `variant="highlight"` like every other tile in the strip, so it inherits
 * the eyebrow label, the clickable chrome, and the hover contract for free. It carries no
 * `topRight` icon: the faces are the tile's visual identity, and a generic person glyph beside them
 * would only repeat it.
 *
 * `entryLabel` goes into the tile's own entry-line slot — the same bottom-anchored orange line the
 * stat tiles use — so this tile's "way in" sits on one baseline with theirs across the row, and the
 * arrow is drawn by the tile rather than written into the string.
 *
 * Domain-free by design — it renders whatever `members` it is handed and calls `onClick`. The
 * dashboard callsite owns both the copy and what a click means (see `EventHighlights`).
 */
export const PunchupLineupCard: FC<LineupCardProps> = memo(({ members, title, entryLabel, onClick, dataCy }) => {
	/* PunchupTile is memoised — a fresh node every render would miss the memo, so the cluster is
	   rebuilt only when the `members` identity changes. */
	const cluster = useMemo(() => {
		const visible = members.slice(0, MAX_VISIBLE_AVATARS);
		const overflow = members.length - visible.length;
		return (
			<span className={styles.cluster}>
				{visible.map((member) => (
					<LineupAvatar key={member.id} member={member} />
				))}
				{overflow > 0 && (
					<span className={`${styles.avatar} ${styles.overflowCount}`} title={`${overflow} more`}>
						+{overflow}
					</span>
				)}
			</span>
		);
	}, [members]);

	return (
		<PunchupTile
			variant="highlight"
			title={title}
			value={cluster}
			entryLabel={entryLabel}
			onClick={onClick}
			dataCy={dataCy}
		/>
	);
});

PunchupLineupCard.displayName = 'PunchupLineupCard';
